/**
 * Wrapper component that makes widgets draggable in edit mode.
 * Shows a drag handle and visual indicator when in edit mode.
 */

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DraggableWidgetProps {
  id: string;
  isEditMode: boolean;
  children: React.ReactNode;
  className?: string;
}

export function DraggableWidget({
  id,
  isEditMode,
  children,
  className,
}: DraggableWidgetProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !isEditMode });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'relative',
        isDragging && 'opacity-50 z-50',
        isEditMode && 'ring-2 ring-dashed ring-primary/30 rounded-xl',
        className
      )}
    >
      {isEditMode && (
        <button
          {...attributes}
          {...listeners}
          className={cn(
            'absolute -left-3 top-1/2 -translate-y-1/2 z-10',
            'p-1.5 bg-background border rounded-md shadow-sm',
            'cursor-grab active:cursor-grabbing',
            'hover:bg-accent hover:border-primary/50',
            'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
            'transition-colors'
          )}
          aria-label="Drag to reorder widget"
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </button>
      )}
      {children}
    </div>
  );
}
